import { STAGE_CHIP, STAGE_LABEL_FR, type Stage } from '@crm/shared';
import { useEffect, useRef, useState } from 'react';

export type ColumnFilterState =
  | { type: 'text'; query: string }
  | { type: 'multi'; selected: string[] }
  | { type: 'multi-status'; selected: string[] }
  | { type: 'range'; min: number | null; max: number | null };

type Props = {
  columnKey: string;
  filterType: 'text' | 'multi' | 'range' | 'multi-status' | 'none';
  align: 'left' | 'right';
  value: ColumnFilterState | null;
  distinct: string[];
  onChange: (state: ColumnFilterState | null) => void;
};

/**
 * Funnel-icon popover hosted in each table header. Encapsulates four
 * filter shapes (text, multi-select, multi-status, numeric range) so the
 * table column definitions only need to declare an intent.
 */
export function PipelineColumnFilter({
  columnKey,
  filterType,
  align,
  value,
  distinct,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const active = value != null;

  return (
    <span className={'inline-block ' + (align === 'right' ? 'ml-1' : 'ml-2')} ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label={`Filter column ${columnKey}`}
        className={
          'inline-flex items-center justify-center w-5 h-5 rounded transition-colors ' +
          (active ? 'bg-signal text-white' : 'text-white/40 hover:text-white hover:bg-white/10')
        }
      >
        <FunnelIcon />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={`Filter ${columnKey}`}
          className={
            'absolute mt-2 z-40 bg-surface text-ink rounded-card border border-platinum shadow-panel p-3 min-w-[220px] ' +
            (align === 'right' ? 'right-2' : 'left-2')
          }
          onClick={(e) => e.stopPropagation()}
        >
          {filterType === 'text' && (
            <TextFilter value={value} onChange={onChange} onClose={() => setOpen(false)} />
          )}
          {filterType === 'multi' && (
            <MultiFilter
              value={value}
              distinct={distinct}
              onChange={onChange}
              onClose={() => setOpen(false)}
            />
          )}
          {filterType === 'multi-status' && (
            <MultiStatusFilter
              value={value}
              distinct={distinct}
              onChange={onChange}
              onClose={() => setOpen(false)}
            />
          )}
          {filterType === 'range' && (
            <RangeFilter value={value} onChange={onChange} onClose={() => setOpen(false)} />
          )}
        </div>
      )}
    </span>
  );
}

function FunnelIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M2 2h8l-3.2 4v3l-1.6.8V6L2 2z" stroke="currentColor" strokeWidth="1" fill="none" />
    </svg>
  );
}

function TextFilter({
  value,
  onChange,
  onClose,
}: {
  value: ColumnFilterState | null;
  onChange: (s: ColumnFilterState | null) => void;
  onClose: () => void;
}) {
  const initial = value?.type === 'text' ? value.query : '';
  const [q, setQ] = useState(initial);
  return (
    <div className="space-y-2">
      <label className="block text-xs text-ink-secondary uppercase tracking-wider">Search</label>
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="input-base text-[12px]"
        placeholder="Contains…"
      />
      <FilterActions
        onApply={() => {
          onChange(q ? { type: 'text', query: q } : null);
          onClose();
        }}
        onClear={() => {
          setQ('');
          onChange(null);
          onClose();
        }}
      />
    </div>
  );
}

function MultiFilter({
  value,
  distinct,
  onChange,
  onClose,
}: {
  value: ColumnFilterState | null;
  distinct: string[];
  onChange: (s: ColumnFilterState | null) => void;
  onClose: () => void;
}) {
  const initial = value?.type === 'multi' ? value.selected : [];
  const [selected, setSelected] = useState<string[]>(initial);
  function toggle(v: string) {
    setSelected((curr) => (curr.includes(v) ? curr.filter((x) => x !== v) : [...curr, v]));
  }
  return (
    <div className="space-y-2 max-h-[260px] overflow-y-auto">
      <p className="text-xs text-ink-secondary uppercase tracking-wider">Values</p>
      {distinct.length === 0 && <p className="text-xs italic text-ink-tertiary">None.</p>}
      <ul className="space-y-1">
        {distinct.map((v) => (
          <li key={v}>
            <label className="flex items-center gap-2 text-[12px]">
              <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />
              <span>{v || <em className="text-ink-tertiary">(empty)</em>}</span>
            </label>
          </li>
        ))}
      </ul>
      <FilterActions
        onApply={() => {
          onChange(selected.length ? { type: 'multi', selected } : null);
          onClose();
        }}
        onClear={() => {
          setSelected([]);
          onChange(null);
          onClose();
        }}
      />
    </div>
  );
}

function MultiStatusFilter({
  value,
  distinct,
  onChange,
  onClose,
}: {
  value: ColumnFilterState | null;
  distinct: string[];
  onChange: (s: ColumnFilterState | null) => void;
  onClose: () => void;
}) {
  const initial = value?.type === 'multi-status' ? value.selected : [];
  const [selected, setSelected] = useState<string[]>(initial);
  function toggle(v: string) {
    setSelected((curr) => (curr.includes(v) ? curr.filter((x) => x !== v) : [...curr, v]));
  }
  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-secondary uppercase tracking-wider">Status</p>
      <ul className="space-y-1">
        {distinct.map((v) => {
          const chip = STAGE_CHIP[v as Stage];
          return (
            <li key={v}>
              <label className="flex items-center gap-2 text-[12px]">
                <input type="checkbox" checked={selected.includes(v)} onChange={() => toggle(v)} />
                <span
                  className="inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-full font-medium"
                  style={chip ? { background: chip.bg, color: chip.fg } : {}}
                >
                  {STAGE_LABEL_FR[v as Stage] ?? v}
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      <FilterActions
        onApply={() => {
          onChange(selected.length ? { type: 'multi-status', selected } : null);
          onClose();
        }}
        onClear={() => {
          setSelected([]);
          onChange(null);
          onClose();
        }}
      />
    </div>
  );
}

function RangeFilter({
  value,
  onChange,
  onClose,
}: {
  value: ColumnFilterState | null;
  onChange: (s: ColumnFilterState | null) => void;
  onClose: () => void;
}) {
  const initial = value?.type === 'range' ? value : { min: null, max: null };
  const [min, setMin] = useState<string>(initial.min == null ? '' : String(initial.min));
  const [max, setMax] = useState<string>(initial.max == null ? '' : String(initial.max));
  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-secondary uppercase tracking-wider">Plage</p>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          value={min}
          onChange={(e) => setMin(e.target.value)}
          placeholder="Min"
          className="input-base text-[12px] font-tabular"
        />
        <input
          type="number"
          value={max}
          onChange={(e) => setMax(e.target.value)}
          placeholder="Max"
          className="input-base text-[12px] font-tabular"
        />
      </div>
      <FilterActions
        onApply={() => {
          const parsedMin = min === '' ? null : Number(min);
          const parsedMax = max === '' ? null : Number(max);
          if (parsedMin == null && parsedMax == null) onChange(null);
          else onChange({ type: 'range', min: parsedMin, max: parsedMax });
          onClose();
        }}
        onClear={() => {
          setMin('');
          setMax('');
          onChange(null);
          onClose();
        }}
      />
    </div>
  );
}

function FilterActions({ onApply, onClear }: { onApply: () => void; onClear: () => void }) {
  return (
    <div className="flex items-center justify-between pt-2 border-t border-platinum">
      <button
        type="button"
        onClick={onClear}
        className="text-[11px] uppercase tracking-wider text-ink-tertiary hover:text-ink"
      >
        Effacer
      </button>
      <button
        type="button"
        onClick={onApply}
        className="px-3 py-1 text-[11px] uppercase tracking-wider bg-graphite text-white rounded"
      >
        Appliquer
      </button>
    </div>
  );
}
